import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, notification } from 'antd';

import useAuth from '@hooks/use-auth';

import styles from './index.module.scss';

const AuthCallback: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          notification.error({
            message: 'Authentication failed',
            description: errorDescription || error,
          });
          navigate('/auth/login', { replace: true });
          return;
        }

        const savedState = sessionStorage.getItem('microsoft_oauth_state');
        if (!state || state !== savedState) {
          notification.error({
            message: 'Authentication failed',
            description: 'Security validation failed',
          });
          navigate('/auth/login', { replace: true });
          return;
        }

        sessionStorage.removeItem('microsoft_oauth_state');

        if (!code) {
          notification.error({
            message: 'Authentication failed',
            description: 'No authorization code received from Microsoft',
          });
          navigate('/auth/login', { replace: true });
          return;
        }

        const codeVerifier = sessionStorage.getItem('microsoft_code_verifier');
        if (!codeVerifier) {
          notification.error({
            message: 'Authentication failed',
            description: 'Security validation failed',
          });
          navigate('/auth/login', { replace: true });
          return;
        }

        sessionStorage.removeItem('microsoft_code_verifier');

        await login(`${code}:${codeVerifier}`, 'microsoft');
        navigate('/', { replace: true });
      } catch (error: any) {
        console.error('Microsoft callback error:', error);
        notification.error({
          message: 'Authentication failed',
          description:
            error.message || 'Unable to complete Microsoft authentication',
        });
        navigate('/auth/login', { replace: true });
      } finally {
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, login, navigate]);
  return (
    <div className={styles.container}>
      <Spin spinning={isProcessing} size="large">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2>Completing Microsoft sign-in...</h2>
          <p>Please wait while we authenticate your account.</p>
        </div>
      </Spin>
    </div>
  );
};

export default AuthCallback;
